import { Spinner } from '@frontend/components/common';
import { CommonMessage } from '@frontend/lang';
import { LuCheck } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';

import LargeSlotCard from '../../../components/slot-cards/large-slot-card.component';
import SmallSlotCard from '../../../components/slot-cards/small-slot-card.component';
import WizardScreenContainer from '../../components/wizard-screen-container/wizard-screen-container.component';
import { useReviewRecyclesConfirmOpenSlot } from './review-recycles-confirm-open-slot.controller';

const ID = 'review-recycles-confirm-open-slot';

const SLOT_THRESHOLD = 6;

const ReviewRecyclesConfirmOpenSlot = () => {
    const viewProps = useReviewRecyclesConfirmOpenSlot();

    const renderSlotCards = () => {
        const slotsWithResolved = viewProps.slots.filter((s) => s.slot !== null);

        if (slotsWithResolved.length <= SLOT_THRESHOLD) {
            return (
                <div className='flex flex-col items-start justify-start gap-2 self-stretch'>
                    {slotsWithResolved.map((s) =>
                        s.slot ? (
                            <LargeSlotCard
                                key={s.slotId}
                                slot={s.slot}
                                status={viewProps.slotStatusMap.get(s.slotId)}
                            />
                        ) : null
                    )}
                </div>
            );
        }

        return (
            <div className='grid grid-cols-2 gap-2 self-stretch'>
                {slotsWithResolved.map((s) =>
                    s.slot ? (
                        <SmallSlotCard
                            key={s.slotId}
                            slot={s.slot}
                            status={viewProps.slotStatusMap.get(s.slotId)}
                        />
                    ) : null
                )}
            </div>
        );
    };

    const renderContent = () => {
        if (viewProps.phase === 'loading') {
            return (
                <div className='flex size-full items-center justify-center'>
                    <Spinner />
                </div>
            );
        }

        if (viewProps.phase === 'processing' && viewProps.allSlotsClosed) {
            return (
                <div className='bg-background-surface border-border-default inline-flex flex-col items-center justify-start gap-4 self-stretch rounded-xl border p-4'>
                    <div className='bg-status-success flex size-9 items-center justify-center rounded-full'>
                        <LuCheck className='text-background-surface size-6' />
                    </div>
                    <div className='flex flex-col items-center justify-start gap-1 self-stretch'>
                        <div className='text-foreground-default justify-start text-base font-semibold'>
                            <FormattedMessage
                                defaultMessage='All SLOTs are now closed'
                                id='Wizard.ProcessOpenCloseSlots.AllSlotsClosed'
                                description='Message displayed to inform the user that all SLOTs are now closed.'
                            />
                        </div>
                        <div className='text-brand-muted justify-start self-stretch text-center text-sm font-medium'>
                            <FormattedMessage
                                defaultMessage='Thank you!'
                                id='Wizard.ProcessOpenCloseSlot.ExitInstruction'
                                description='Instruction to the user that they can exit now after closing the slot.'
                            />
                        </div>
                    </div>
                </div>
            );
        }

        if (viewProps.phase === 'confirm' && viewProps.slots.length === 0) {
            return (
                <div className='flex size-full flex-col items-center justify-center gap-3 py-12'>
                    <LuCheck
                        size={64}
                        className='text-foreground-secondary'
                    />
                    <span className='text-foreground-secondary text-lg font-medium'>
                        <FormattedMessage
                            id='kiosk.review-recycles-confirm.no-recycles'
                            defaultMessage='There are currently no recycles to be reviewed'
                        />
                    </span>
                </div>
            );
        }

        return renderSlotCards();
    };

    const getFooterProps = () => {
        if (viewProps.phase === 'confirm') {
            return {
                onSubmit: viewProps.onOpenSlots,
                submitDisabled: viewProps.submitDisabled,
                submitLabel:
                    viewProps.slotCount > 1 ? (
                        <FormattedMessage
                            id='kiosk.manage-spot.links.open-slots'
                            defaultMessage='Open SLOTs'
                        />
                    ) : (
                        <FormattedMessage
                            id='kiosk.wizard.drop-off-confirm-open-slot.open-slot'
                            defaultMessage='Open SLOT'
                            description='Button text to open the slot'
                        />
                    )
            };
        }

        return {
            onSubmit: viewProps.onSubmit,
            submitDisabled: viewProps.submitDisabled,
            submitLabel: CommonMessage.BUTTONS.CONTINUE
        };
    };

    return (
        <WizardScreenContainer
            id={ID}
            header={{
                title: (
                    <FormattedMessage
                        id='kiosk.wizard.review-recycles-confirm.title'
                        defaultMessage='Review recycles'
                    />
                )
            }}
            footer={getFooterProps()}
            containerClassName='bg-background-default flex flex-1 flex-col items-center justify-start gap-3 self-stretch overflow-y-auto p-6'>
            {renderContent()}
        </WizardScreenContainer>
    );
};

export default ReviewRecyclesConfirmOpenSlot;
